import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import MegaMenu from "./MegaMenu";
import MobileMenu from "./MobileMenu";
import SearchResults from "./SearchResults";
import { apiService } from "../services/api";

const HeaderWrapper = styled.header`
  position: relative;
  z-index: 30;
`;

const MainHeader = styled.div`
  border-bottom: 1px solid #e5e5e5;
  padding: 20px 0 16px;
  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1300px;
  }

  @media (max-width: 1024px) {
    padding: 20px 0px;

    .container {
      display: grid;
      grid-template-columns: auto 1fr auto auto auto;
      gap: 15px;
      align-items: center;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: #000;
  background: none;
  border: none;
  cursor: pointer;

  @media (max-width: 1024px) {
    display: flex;
    grid-column: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  height: auto;

  img {
    width: 60px;
    height: auto;
    flex-shrink: 0;
    vertical-align: middle;
  }

  span.title {
    color: #363636ff;
    white-space: nowrap;
    font-weight: 500;
    font-size: 14px;
    line-height: 1;
    display: inline-block;
    vertical-align: middle;
    margin: 0;
    padding: 0;
  }

  @media (max-width: 1024px) {
    grid-column: 2;
    justify-content: center;

    img {
      width: 35px;
    }

    span.title {
      font-size: 14px;
      font-weight: 600;
    }
  }

  @media (min-width: 1024px) {
    gap: 18px;
    img {
      width: 60px;
    }
    span.title {
      font-size: 20px;
    }
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 20px;

  @media (max-width: 1024px) {
    display: none; // Hide desktop search on mobile
  }
`;

const SearchForm = styled.form`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 50px 0 20px;
  border: 1px solid #000000ff;
  border-radius: 50px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #777;
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.6);
  }

  @media (min-width: 1280px) {
    height: 48px;
    padding: 0 60px 0 20px;
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 3px;
  top: 5px;
  width: 30px;
  height: 30px;
  background: #ff6720;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
    color: white;
  }

  @media (min-width: 1280px) {
    right: 6px;
    top: 6px;
    width: 36px;
    height: 36px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const MobileSearchButton = styled.button`
  display: none;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: #000;

  @media (max-width: 1024px) {
    display: flex;
    grid-column: 3;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: #ff6720;
  }
`;

const MobileLocationButton = styled(Link)`
  display: none;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: #000;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;

  @media (max-width: 1024px) {
    display: flex;
    grid-column: 4;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: #ff6720;
  }
`;

const MobileContactButton = styled(Link)`
  display: none;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: #000;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;

  @media (max-width: 1024px) {
    display: flex;
    grid-column: 5;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: #ff6720;
  }
`;

const ExpandedMobileSearch = styled.div`
  display: ${(props) => (props.isExpanded ? "block" : "none")};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  padding: 15px;
  border-top: 1px solid #e5e5e5;
  z-index: 100;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const MobileSearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 50px 0 20px;
  border: 1px solid #000;
  border-radius: 25px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #ff6720;
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.6);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  @media (max-width: 1024px) {
    display: none; // Hide desktop actions on mobile
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #000;
  position: relative;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: #ff6720;
  }

  @media (max-width: 1280px) {
    &.wishlist {
      display: none;
    }
  }
`;

const Navigation = styled.nav`
  display: none;
  border-bottom: 1px solid #e5e5e5;
  padding-top: 10px;

  @media (min-width: 1024px) {
    display: block;
  }

  .container {
    display: flex;
    justify-content: center;
  }
`;

const NavList = styled.ul`
  display: flex;
  gap: 20px;
  justify-content: center;
  width: 100%;

  @media (min-width: 1024px) {
    gap: 10px;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  color: #000;
  font-family: roboto, sans-serif;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.7;
  }

  &.active {
    opacity: 1;
  }

  svg {
    width: 11px;
    height: 11px;
    color: #999;
    transition: transform 0.3s ease;
  }

  &.active svg {
    transform: rotate(180deg);
  }
`;

const Header = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showMobileSearchResults, setShowMobileSearchResults] = useState(false);

  // Refs for search containers
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const handleMenuHover = (menu) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setActiveMenu(menu);
  };

  const handleMenuLeave = () => {
    const timeout = setTimeout(() => {
      setActiveMenu(null);
    }, 150); // 150ms delay before closing
    setHoverTimeout(timeout);
  };

  const handleMegaMenuEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleMegaMenuLeave = () => {
    setActiveMenu(null);
  };

  // Search functionality
  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await apiService.searchProducts(query, {
        status: "active",
        limit: 12,
      });

      if (response.success) {
        setSearchResults(response.data || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Desktop search handlers
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSearchResults(query.length > 0);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleSearchFocus = () => {
    if (searchQuery.length > 0) {
      setShowSearchResults(true);
    }
  };

  // Mobile search handlers
  const handleMobileSearchChange = (e) => {
    const query = e.target.value;
    setMobileSearchQuery(query);
    setShowMobileSearchResults(query.length > 0);
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(mobileSearchQuery)}`;
    }
  };

  const handleMobileSearchFocus = () => {
    if (mobileSearchQuery.length > 0) {
      setShowMobileSearchResults(true);
    }
  };

  // Close search results when clicking outside
  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowSearchResults(false);
    }
    if (
      mobileSearchRef.current &&
      !mobileSearchRef.current.contains(event.target)
    ) {
      setShowMobileSearchResults(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery || mobileSearchQuery) {
        performSearch(searchQuery || mobileSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, mobileSearchQuery]);

  // Click outside effect
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <HeaderWrapper>
        <MainHeader>
          <div className="container">
            {/* Mobile hamburger menu */}
            <MobileMenuButton
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                </svg>
              )}
            </MobileMenuButton>

            {/* Logo */}
            <Logo>
              <Link to="/">
                <img
                  src="https://res.cloudinary.com/dmyb3ffz9/image/upload/v1756269583/image_1_wfi4lf.webp"
                  to="/"
                  alt="XPLOD GROUPS"
                />
                <span className="title">XPLOD GROUPS</span>
              </Link>
            </Logo>

            {/* Desktop Search - hidden on mobile */}
            <SearchWrapper ref={searchRef}>
              <SearchForm onSubmit={handleSearchSubmit}>
                <SearchInput
                  type="text"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                />
                <SearchButton type="submit">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                </SearchButton>
              </SearchForm>

              <SearchResults
                searchQuery={searchQuery}
                results={searchResults}
                isLoading={isSearching}
                isVisible={showSearchResults}
                onClose={() => setShowSearchResults(false)}
                totalResults={searchResults.length}
              />
            </SearchWrapper>

            {/* Mobile Search Button */}
            <MobileSearchButton
              onClick={() => setIsMobileSearchExpanded(!isMobileSearchExpanded)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </MobileSearchButton>

            {/* Mobile Location Button */}
            <MobileLocationButton to="/branches">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </MobileLocationButton>

            {/* Mobile Contact Button */}
            <MobileContactButton to="/contact">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </MobileContactButton>

            {/* Desktop Actions - hidden on mobile */}
            {/* Desktop actions - always visible on desktop, hidden on mobile */}
            <HeaderActions>
              <ActionButton as={Link} to="/branches">
                {/* Location/branches icon */}
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
              </ActionButton>
              <ActionButton as={Link} to="/contact">
                {/* Message/contact icon */}
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </ActionButton>
            </HeaderActions>
          </div>

          {/* Expanded Mobile Search */}
          <ExpandedMobileSearch isExpanded={isMobileSearchExpanded}>
            <div style={{ position: "relative" }} ref={mobileSearchRef}>
              <form onSubmit={handleMobileSearchSubmit}>
                <MobileSearchInput
                  type="text"
                  placeholder="Search for products..."
                  value={mobileSearchQuery}
                  onChange={handleMobileSearchChange}
                  onFocus={handleMobileSearchFocus}
                />
                <SearchButton
                  type="submit"
                  style={{ position: "absolute", right: "3px", top: "5px" }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                </SearchButton>
              </form>

              <SearchResults
                searchQuery={mobileSearchQuery}
                results={searchResults}
                isLoading={isSearching}
                isVisible={showMobileSearchResults}
                onClose={() => setShowMobileSearchResults(false)}
                totalResults={searchResults.length}
              />
            </div>
          </ExpandedMobileSearch>
        </MainHeader>

        {/* Desktop Navigation - preserved from original */}
        <Navigation>
          <div className="container">
            <NavList onMouseLeave={handleMenuLeave}>
              <NavItem>
                <NavLink
                  to="/collections/chainsaws"
                  onMouseEnter={() => handleMenuHover("chainsaws")}
                  onClick={() => setActiveMenu(null)}
                  className={activeMenu === "chainsaws" ? "active" : ""}
                >
                  Chainsaws
                  <svg fill="none" viewBox="0 0 6 4">
                    <path
                      d="m5.785.53-2.52 2.178L.884.529"
                      stroke="currentColor"
                      strokeWidth=".545"
                    />
                  </svg>
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  to="/collections/brushcutters"
                  onMouseEnter={() => handleMenuHover("brushcutters")}
                  onClick={() => setActiveMenu(null)}
                  className={activeMenu === "brushcutters" ? "active" : ""}
                >
                  Brushcutters
                  <svg fill="none" viewBox="0 0 6 4">
                    <path
                      d="m5.785.53-2.52 2.178L.884.529"
                      stroke="currentColor"
                      strokeWidth=".545"
                    />
                  </svg>
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  to="/collections/blowers"
                  onMouseEnter={() => handleMenuHover("blowers")}
                  onClick={() => setActiveMenu(null)}
                  className={activeMenu === "blowers" ? "active" : ""}
                >
                  Blowers
                  <svg fill="none" viewBox="0 0 6 4">
                    <path
                      d="m5.785.53-2.52 2.178L.884.529"
                      stroke="currentColor"
                      strokeWidth=".545"
                    />
                  </svg>
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  to="/collections/pressure-washers"
                  onMouseEnter={() => handleMenuHover("pressure-washers")}
                  onClick={() => setActiveMenu(null)}
                  className={activeMenu === "pressure-washers" ? "active" : ""}
                >
                  Pressure Washers
                  <svg fill="none" viewBox="0 0 6 4">
                    <path
                      d="m5.785.53-2.52 2.178L.884.529"
                      stroke="currentColor"
                      strokeWidth=".545"
                    />
                  </svg>
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  to="/collections/lawn-mowers"
                  onMouseEnter={() => handleMenuHover("lawn-mowers")}
                  onClick={() => setActiveMenu(null)}
                  className={activeMenu === "lawn-mowers" ? "active" : ""}
                >
                  Lawn Mowers
                  <svg fill="none" viewBox="0 0 6 4">
                    <path
                      d="m5.785.53-2.52 2.178L.884.529"
                      stroke="currentColor"
                      strokeWidth=".545"
                    />
                  </svg>
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink to="/branches">Our Locations</NavLink>
              </NavItem>

              <NavItem>
                <NavLink to="/about">About Us</NavLink>
              </NavItem>
            </NavList>
          </div>
        </Navigation>

        <MegaMenu
          activeMenu={activeMenu}
          onMouseEnter={handleMegaMenuEnter}
          onMouseLeave={handleMegaMenuLeave}
          onClose={() => setActiveMenu(null)}
        />
      </HeaderWrapper>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </>
  );
};

export default Header;
